import { TypedDocumentNode, gql } from '@apollo/client';
import { faArrowRightArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';

import {
  PlayerFieldStatus,
  Position,
  ScoreStatus,
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { OverlapItems } from '@sorare/core/src/atoms/ui/OverlapItems';
import { PlayerAppearance } from '@sorare/core/src/components/cards/PlayerAppearance';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { isType, isTypeFn } from '@sorare/core/src/gql';
import { range } from '@sorare/core/src/lib/arrays';

import { So5AppearanceScore } from 'components/appearance/So5AppearanceScore';
import { PlayerGameStatsPrediction } from 'components/composeTeam/PlayerGameStatsPrediction';
import { InjuryTag } from 'components/player/InjuryTag';
import Captain from 'components/so5/Captain';
import { So5AppearenceInlineGames } from 'components/stats/So5AppearenceInlineGames';
import { So5AppearenceInlineGamesDots } from 'components/stats/So5AppearenceInlineGamesDots';
import { SubIcon } from 'components/stats/SubIcon';
import {
  getDecisiveActions,
  isAppearanceLocked,
  isFixtureLive,
  isFixtureOpened,
  isGameLive,
} from 'lib/so5';

import { PlayerCard_so5Appearance } from './__generated__/index.graphql';

const DefaultFooter = styled(Vertical).attrs({ center: true })``;

type Props = {
  onClick?: () => void;
  appearance: PlayerCard_so5Appearance;
  showScore?: boolean;
  withDate?: boolean;
  withInlineGamesDots?: boolean;
  Footer?: FC<{ children: ReactNode }>;
};

export const PlayerCard = ({
  onClick,
  appearance,
  showScore,
  withDate,
  withInlineGamesDots,
  Footer,
}: Props) => {
  const { eligiblePlayerGameScores, captain, anyPlayer } = appearance;

  const anyGameLive = eligiblePlayerGameScores.some(pgs =>
    isGameLive(pgs.anyGame.status)
  );

  const isLive = isFixtureLive(appearance.so5Fixture);
  const isUpcoming = isFixtureOpened(appearance.so5Fixture);

  const isStartingPitcher = eligiblePlayerGameScores.some(
    pgs => pgs.positionTyped === Position.baseball_starting_pitcher
  );
  const pitchingGames = eligiblePlayerGameScores.filter(
    pgs =>
      isType(pgs.anyGame, 'GameOfBaseball') &&
      pgs.anyGame.probablePitchers.find(p => p.slug === anyPlayer.slug)
  );

  const FooterWrapper = Footer || DefaultFooter;

  // player is technically not DNP but we can confidently assume they will be
  const scoreIsPendingButWillBeDNP =
    isStartingPitcher && pitchingGames.length === 0;

  const decisiveActions = eligiblePlayerGameScores.reduce<
    Record<string, number>
  >((acc, pgs) => {
    const pgsDecisiveActions = getDecisiveActions(pgs);
    Object.entries(pgsDecisiveActions).forEach(([key, value]) => {
      acc[key] = (acc[key] || 0) + (value || 0);
    });

    return acc;
  }, {});

  const hasDecisiveActions = Object.values(decisiveActions).some(Boolean);

  const footballPlayerGameStats = eligiblePlayerGameScores
    .map(pgs => pgs.anyPlayerGameStats)
    .filter(isTypeFn('PlayerGameStats'));
  const substitutedOut = footballPlayerGameStats.some(
    pgs => pgs.fieldStatus === PlayerFieldStatus.SUBSTITUTED
  );
  const substitutedIn = footballPlayerGameStats.some(
    pgs => pgs.fieldStatus === PlayerFieldStatus.ON_FIELD && !pgs.gameStarted
  );

  const isLocked = isAppearanceLocked(appearance);
  const canBeSwapped =
    isLive &&
    !isLocked &&
    appearance.so5Fixture.eventType.toUpperCase() === So5FixtureEvent.DAILY;

  return (
    <PlayerAppearance
      onClick={onClick}
      disabled={!eligiblePlayerGameScores.length}
      appearance={appearance}
      captain={captain && <Captain active size={2} />}
      topBadge={
        <>
          {substitutedOut && <SubIcon type="sub_out" />}
          {substitutedIn && <SubIcon type="sub_in" />}
        </>
      }
      bottomBadge={
        <Horizontal center>
          {(isUpcoming || isLive) && <InjuryTag player={anyPlayer} />}
          {!isUpcoming && hasDecisiveActions && (
            <OverlapItems offset={8}>
              {Object.entries(decisiveActions).map(([key, value]) =>
                range(value || 0).map(i => (
                  <StatIcon key={`${key}-${i}`} statKey={key} />
                ))
              )}
            </OverlapItems>
          )}
          {isUpcoming &&
            footballPlayerGameStats[0]?.footballPlayingStatusOdds && (
              <PlayerGameStatsPrediction
                variant="chip"
                playerGameStats={footballPlayerGameStats[0]}
              />
            )}
        </Horizontal>
      }
      score={
        <FooterWrapper>
          <Horizontal gap={0.5}>
            {!showScore && anyGameLive && !withInlineGamesDots && (
              <LiveDot size="sm" animate />
            )}
            {showScore && (
              <Horizontal gap={0.5}>
                {anyGameLive && !withInlineGamesDots && (
                  <LiveDot size="sm" animate />
                )}
                {canBeSwapped ? (
                  <Chip size="large">
                    <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                  </Chip>
                ) : (
                  <So5AppearanceScore
                    hideReviewing
                    withDate={withDate}
                    so5Appearance={appearance}
                    forceScoreStatus={
                      scoreIsPendingButWillBeDNP
                        ? ScoreStatus.DID_NOT_PLAY
                        : undefined
                    }
                  />
                )}
              </Horizontal>
            )}
          </Horizontal>
          {withInlineGamesDots ? (
            <So5AppearenceInlineGamesDots
              isStartingPitcher={isStartingPitcher}
              sizeUnit={anyPlayer.sport === Sport.BASEBALL ? 'md' : 'lg'}
              pitchingGames={pitchingGames}
              so5Appearance={appearance}
            />
          ) : (
            <So5AppearenceInlineGames so5Appearance={appearance} />
          )}
        </FooterWrapper>
      }
    />
  );
};

PlayerCard.fragments = {
  so5Appearance: gql`
    fragment PlayerCard_so5Appearance on So5Appearance {
      id
      captain
      pictureUrl
      anyPlayer {
        slug
        sport
        ...InjuryTag_anyPlayer
      }
      eligiblePlayerGameScores {
        id
        positionTyped
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            gameStarted
            fieldStatus
            footballPlayingStatusOdds {
              reliability
            }
            ...PlayerGameStatsPrediction_playerGameStats
          }
        }
        anyGame {
          id
          status: statusTyped
          ... on GameOfBaseball {
            id
            probablePitchers {
              slug
            }
          }
        }
        ...getDecisiveActions_playerGameScoreInterface
      }
      so5Fixture {
        slug
        eventType
        ...isFixtureOpened_so5Fixture
        ...isFixtureLive_so5Fixture
      }
      ...So5AppearanceScore_so5Appearance
      ...So5AppearenceInlineGames_so5Appearance
      ...So5AppearenceInlineGamesDots_so5Appearance
      ...PlayerAppearance_so5Appearance
      ...isAppearanceLocked_so5Appearance
    }
    ${So5AppearanceScore.fragments.so5Appearance}
    ${So5AppearenceInlineGames.fragments.so5Appearance}
    ${So5AppearenceInlineGamesDots.fragments.so5Appearance}
    ${InjuryTag.fragments.anyPlayer}
    ${getDecisiveActions.fragments.playerGameScoreInterface}
    ${PlayerGameStatsPrediction.fragments.playerGameStats}
    ${PlayerAppearance.fragments.so5Appearance}
    ${isAppearanceLocked.fragments.so5Appearance}
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
  ` as TypedDocumentNode<PlayerCard_so5Appearance>,
};
